import React, { useEffect, useState, useCallback, useMemo } from "react";
import {
    Box, Paper, Table, TableHead, TableRow, TableCell, TableBody,
    TablePagination, TextField, MenuItem, Typography, Chip, Button,
    Dialog, DialogTitle, DialogContent, DialogActions, Grid, Avatar,
    Tabs, Tab, Divider, Stack, Card, CardContent, IconButton, Tooltip,
    LinearProgress, Stepper, Step, StepLabel, StepConnector,
    stepConnectorClasses, Alert, AlertTitle, InputAdornment, CircularProgress,
    Badge, Collapse, ButtonGroup,
} from "@mui/material";
import {
    Search, Refresh, Visibility, CheckCircle, Cancel, LocalShipping,
    Inventory, AttachMoney, HourglassEmpty, CloudUpload, DeleteOutline,
    CalendarToday, Phone, Email, LocationOn, Receipt, FilterList,
    PlayArrow, QrCode2, Label, Download, OpenInNew, Rocket, Refresh as RefreshIcon,
    ContentCopy, ExpandMore, ExpandLess, FlightTakeoff, FiberManualRecord,
    AssignmentReturn, Print, Link, Info,
} from "@mui/icons-material";
import { styled, alpha } from "@mui/material/styles";
import { toast } from "react-toastify";
import apiClient from "../utils/apiClient";
import { getImageFullUrl } from "../utils/common";
import { useParams } from "react-router-dom";

// ─── Constants ────────────────────────────────────────────────────────────────

const STATUS_FLOW = ["pending", "confirmed", "processing", "shipped", "delivered", "cancelled", "returned"];

const STATUS_CONFIG = {
    pending: { color: "warning", label: "Pending", icon: <HourglassEmpty sx={{ fontSize: 14 }} /> },
    confirmed: { color: "info", label: "Confirmed", icon: <CheckCircle sx={{ fontSize: 14 }} /> },
    processing: { color: "secondary", label: "Processing", icon: <Inventory sx={{ fontSize: 14 }} /> },
    shipped: { color: "primary", label: "Shipped", icon: <LocalShipping sx={{ fontSize: 14 }} /> },
    delivered: { color: "success", label: "Delivered", icon: <CheckCircle sx={{ fontSize: 14 }} /> },
    cancelled: { color: "error", label: "Cancelled", icon: <Cancel sx={{ fontSize: 14 }} /> },
    returned: { color: "error", label: "Returned", icon: <Cancel sx={{ fontSize: 14 }} /> },
};

const PAYMENT_CONFIG = {
    paid: { color: "success", label: "Paid" },
    pending: { color: "warning", label: "Pending" },
    failed: { color: "error", label: "Failed" },
    refunded: { color: "info", label: "Refunded" },
};

const PAYMENT_CONFIG_TYPE = {
    cod: { color: "warning", label: "COD" },
    razorpay: { color: "success", label: "Paid" },
    //   failed:   { color: "error",   label: "Failed" },
    //   refunded: { color: "info",    label: "Refunded" },
};

const TIMELINE_STEPS = [
    { label: "Order Placed", field: "createdAt" },
    { label: "Confirmed", field: "confirmedAt" },
    { label: "Processing", field: "processingAt" },
    { label: "Shipped", field: "shippedAt" },
    { label: "Delivered", field: "deliveredAt" },
];

// shipping status badge colours
const SHIPPING_STATUS_CONFIG = {
    // 🆕 Initial
    new: { color: "default", label: "New" },
    pending: { color: "warning", label: "Pending" },

    // 📦 Pre-shipment
    ready_to_ship: { color: "info", label: "Ready to Ship" },

    // 🚚 Pickup
    pickup_scheduled: { color: "info", label: "Pickup Scheduled" },
    pickup_failed: { color: "error", label: "Pickup Failed" },

    // 🚛 Shipping flow
    shipped: { color: "primary", label: "Shipped" },
    in_transit: { color: "primary", label: "In Transit" },
    out_for_delivery: { color: "secondary", label: "Out for Delivery" },

    // ✅ Final states
    delivered: { color: "success", label: "Delivered" },
    cancelled: { color: "error", label: "Cancelled" },

    // 🔁 Return / failure
    rto: { color: "error", label: "RTO (Return to Origin)" },
    lost: { color: "error", label: "Lost" },

    // ❓ fallback
    unknown: { color: "default", label: "Unknown" },
};
// ─── Styled Components ────────────────────────────────────────────────────────

const StyledConnector = styled(StepConnector)(({ theme }) => ({
    [`&.${stepConnectorClasses.alternativeLabel}`]: { top: 17 },
    [`&.${stepConnectorClasses.active} .${stepConnectorClasses.line}`]: { borderColor: theme.palette.primary.main },
    [`&.${stepConnectorClasses.completed} .${stepConnectorClasses.line}`]: { borderColor: theme.palette.primary.main },
    [`& .${stepConnectorClasses.line}`]: {
        borderColor: theme.palette.divider,
        borderTopWidth: 2,
        borderRadius: 1,
    },
}));

const StepIconRoot = styled("div")(({ theme, ownerState }) => ({
    width: 36, height: 36, borderRadius: "50%",
    display: "flex", alignItems: "center", justifyContent: "center",
    fontSize: 13, fontWeight: 700,
    border: `2px solid ${ownerState.completed || ownerState.active ? theme.palette.primary.main : theme.palette.divider}`,
    background: ownerState.completed
        ? theme.palette.primary.main
        : ownerState.active
            ? alpha(theme.palette.primary.main, 0.1)
            : theme.palette.background.paper,
    color: ownerState.completed ? "#fff" : ownerState.active ? theme.palette.primary.main : theme.palette.text.disabled,
    transition: "all .25s",
}));

function StepIcon({ active, completed, icon }) {
    return (
        <StepIconRoot ownerState={{ active, completed }}>
            {completed ? <CheckCircle sx={{ fontSize: 18 }} /> : icon}
        </StepIconRoot>
    );
}

const HoverRow = styled(TableRow)(({ theme }) => ({
    cursor: "pointer",
    transition: "background .15s",
    "&:hover": { background: alpha(theme.palette.primary.main, 0.04) },
    "&:last-child td": { border: 0 },
}));

const UploadZone = styled(Box)(({ theme }) => ({
    border: `2px dashed ${theme.palette.primary.light}`,
    borderRadius: 12,
    padding: theme.spacing(3),
    textAlign: "center",
    cursor: "pointer",
    background: alpha(theme.palette.primary.main, 0.03),
    transition: "all .2s",
    "&:hover": {
        background: alpha(theme.palette.primary.main, 0.07),
        borderColor: theme.palette.primary.main,
    },
    width: "100%",
}));

const ShiprocketCard = styled(Paper)(({ theme }) => ({
    borderRadius: 14,
    border: `1px solid ${theme.palette.divider}`,
    borderLeft: `4px solid #f77f00`,
    padding: theme.spacing(2.5),
    boxShadow: "none",
    background: alpha("#f77f00", 0.02),
}));

const InfoRow = ({ label, value, copyable, link, mono }) => {
    const copy = () => { navigator.clipboard.writeText(value); toast.success("Copied!"); };
    return (
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ py: 0.7, borderBottom: "1px solid", borderColor: "divider" }}>
            <Typography variant="body2" color="text.secondary" sx={{ minWidth: 140, flexShrink: 0 }}>{label}</Typography>
            <Stack direction="row" alignItems="center" spacing={0.5}>
                <Typography
                    variant="body2"
                    fontWeight={500}
                    fontFamily={mono ? "monospace" : undefined}
                    sx={{ wordBreak: "break-all", textAlign: "right" }}
                >
                    {value || "—"}
                </Typography>
                {copyable && value && (
                    <Tooltip title="Copy"><IconButton size="small" onClick={copy}><ContentCopy sx={{ fontSize: 13 }} /></IconButton></Tooltip>
                )}
                {link && value && (
                    <Tooltip title="Open">
                        <IconButton size="small" onClick={() => window.open(value, "_blank")}><OpenInNew sx={{ fontSize: 13 }} /></IconButton>
                    </Tooltip>
                )}
            </Stack>
        </Stack>
    );
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

const fmt = (v) =>
    v ? new Date(v).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : null;

const fmtDate = (v) =>
    v ? new Date(v).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—";

const parseJSON = (str, fallback = []) => {
    try { return typeof str === "string" ? JSON.parse(str) : str || fallback; }
    catch { return fallback; }
};

const initials = (name = "") =>
    (name || "?").split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase();

const canCancelOrder = (order) => ["pending", "confirmed", "processing"].includes(order.order_status);
// ─── Section Title ────────────────────────────────────────────────────────────

function SectionTitle({ children, icon }) {
    return (
        <Stack direction="row" alignItems="center" spacing={0.7} sx={{ mb: 1.5 }}>
            {icon && <Box sx={{ color: "text.disabled", display: "flex", alignItems: "center" }}>{icon}</Box>}
            <Typography variant="overline" color="text.secondary" fontWeight={700} letterSpacing={1.2}>
                {children}
            </Typography>
        </Stack>
    );
}

// ─── Order Timeline ───────────────────────────────────────────────────────────

function OrderTimeline({ order }) {
    const curIdx = STATUS_FLOW.indexOf(order.order_status);
    const cancelled = order.order_status === "cancelled" || order.order_status === "returned";

    return (
        <Box>
            <SectionTitle icon={<CalendarToday sx={{ fontSize: 14 }} />}>Order Journey</SectionTitle>

            {cancelled && (
                <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
                    <AlertTitle>Order {order.order_status === "cancelled" ? "Cancelled" : "Returned"}</AlertTitle>
                    {order.cancelReason || "No reason provided."}
                    {(order.cancelledAt || order.returnedAt) && (
                        <Typography variant="caption" display="block" mt={0.5}>
                            At: {fmt(order.cancelledAt || order.returnedAt)}
                        </Typography>
                    )}
                </Alert>
            )}

            <Stepper activeStep={cancelled ? -1 : curIdx} alternativeLabel connector={<StyledConnector />} sx={{ px: 0 }}>
                {TIMELINE_STEPS.map((step, i) => {
                    const date = order[step.field];
                    const completed = !cancelled && i < curIdx;
                    const active = !cancelled && i === curIdx;
                    return (
                        <Step key={step.label} completed={completed}>
                            <StepLabel StepIconComponent={(props) => <StepIcon {...props} icon={i + 1} />}>
                                <Typography variant="caption" fontWeight={active ? 700 : 500} color={active ? "primary.main" : "text.primary"}>
                                    {step.label}
                                </Typography>
                                <Typography variant="caption" display="block" color={date ? "text.secondary" : "text.disabled"} sx={{ fontSize: 10, mt: 0.3, lineHeight: 1.4 }}>
                                    {date ? fmt(date) : "—"}
                                </Typography>
                            </StepLabel>
                        </Step>
                    );
                })}
            </Stepper>

            <Box sx={{ mt: 3 }}>
                <SectionTitle icon={<Receipt sx={{ fontSize: 14 }} />}>Date Breakdown</SectionTitle>
                <Paper variant="outlined" sx={{ borderRadius: 2, overflow: "hidden" }}>
                    <Table size="small">
                        <TableHead>
                            <TableRow sx={{ bgcolor: "grey.50" }}>
                                <TableCell sx={{ fontWeight: 700, fontSize: 12, textTransform: "uppercase", letterSpacing: 0.5 }}>Event</TableCell>
                                <TableCell sx={{ fontWeight: 700, fontSize: 12, textTransform: "uppercase", letterSpacing: 0.5 }}>Date & Time</TableCell>
                                <TableCell sx={{ fontWeight: 700, fontSize: 12, textTransform: "uppercase", letterSpacing: 0.5 }}>Status</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {[
                                { event: "Order Placed", date: order.createdAt },
                                { event: "Confirmed", date: order.confirmedAt },
                                { event: "Processing", date: order.processingAt },
                                { event: "Shipped", date: order.shippedAt },
                                { event: "Delivered", date: order.deliveredAt },
                                { event: "Cancelled", date: order.cancelledAt },
                                { event: "Returned", date: order.returnedAt },
                            ]
                                .filter((r) => r.date)
                                .map((r) => (
                                    <TableRow key={r.event}>
                                        <TableCell>
                                            <Stack direction="row" spacing={1} alignItems="center">
                                                <CalendarToday sx={{ fontSize: 13, color: "text.disabled" }} />
                                                <Typography variant="body2" fontWeight={500}>{r.event}</Typography>
                                            </Stack>
                                        </TableCell>
                                        <TableCell><Typography variant="body2" color="text.secondary">{fmt(r.date)}</Typography></TableCell>
                                        <TableCell>
                                            <Chip icon={<CheckCircle sx={{ fontSize: "14px !important" }} />} label="Done" color="success" size="small" variant="outlined" />
                                        </TableCell>
                                    </TableRow>
                                ))}
                        </TableBody>
                    </Table>
                </Paper>
            </Box>

            {order.adminNote && (
                <Alert severity="warning" sx={{ mt: 2, borderRadius: 2 }}>
                    <AlertTitle sx={{ fontSize: 13 }}>Admin Note</AlertTitle>
                    <Typography variant="body2">{order.adminNote}</Typography>
                </Alert>
            )}
        </Box>
    );
}

// ─── Panel ─────────────────────────────────────────────────────────

function ShiprocketPanel({ order, onRefresh }) {

    const hasShiprocketOrder = Boolean(order.shiprocket_order_id);
    const hasAWB = Boolean(order.awb_code);
    const hasLabel = Boolean(order.label_url);
    const hasInvoice = Boolean(order.invoice_url);
    const hasTracking = Boolean(order.tracking_url);

    // Download helpers
    const openUrl = (url) => window.open(url, "_blank");

    const shippingStatus = SHIPPING_STATUS_CONFIG[order.shipping_status] || SHIPPING_STATUS_CONFIG["new"];

    return (
        <Box>
            {/* Header */}
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
                <Stack direction="row" alignItems="center" spacing={1}>
                    <Rocket sx={{ color: "#f77f00", fontSize: 22 }} />
                    <Typography fontWeight={700} fontSize={16}>Shipment Integration</Typography>
                </Stack>
                <Chip
                    label={shippingStatus.label}
                    color={shippingStatus.color}
                    size="small"
                    sx={{ fontWeight: 600 }}
                />
            </Stack>

            {/* Shiprocket IDs & Info */}
            <ShiprocketCard sx={{ mb: 2 }}>
                <SectionTitle icon={<QrCode2 sx={{ fontSize: 14 }} />}>Shipment Details</SectionTitle>
                <InfoRow label=" Order ID" value={order.shiprocket_order_id} copyable mono />
                <InfoRow label=" Shipment" value={order.shiprocket_shipment_id} copyable mono />
                <InfoRow label="AWB Code" value={order.awb_code} copyable mono />
                <InfoRow label="Courier" value={order.courier_name} />
                <InfoRow label="Pickup Date" value={order.pickup_date ? fmtDate(order.pickup_date) : null} />
                <InfoRow label="Est. Delivery" value={order.estimated_delivery ? fmtDate(order.estimated_delivery) : null} />
                <InfoRow label="Delivered Date" value={order.delivered_date ? fmtDate(order.delivered_date) : null} />
                <InfoRow label="Tracking URL" value={order.tracking_url} link />
            </ShiprocketCard>

            {/* Package Dimensions */}
            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, mb: 2 }}>
                <SectionTitle>Package Dimensions & Weight</SectionTitle>
                <Grid container spacing={1.5}>
                    {[
                        { label: "Weight (kg)", value: order.shipping_weight || "—" },
                        { label: "Length (cm)", value: order.shipping_length || "—" },
                        { label: "Width (cm)", value: order.shipping_width || "—" },
                        { label: "Height (cm)", value: order.shipping_height || "—" },
                    ].map(({ label, value }) => (
                        <Grid item xs={6} sm={3} key={label}>
                            <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2, textAlign: "center" }}>
                                <Typography variant="caption" color="text.secondary" display="block">{label}</Typography>
                                <Typography fontWeight={700} fontSize={16}>{value}</Typography>
                            </Paper>
                        </Grid>
                    ))}
                </Grid>
            </Paper>

            {/* Action Buttons */}
            <Box sx={{ mb: 2 }}>
                {/* <SectionTitle>Actions</SectionTitle> */}
                <Stack direction="row" flexWrap="wrap" gap={1.5}>

                    {/* Label Download */}
                    {hasLabel && (
                        <Button
                            variant="outlined"
                            color="secondary"
                            startIcon={<Label />}
                            onClick={() => openUrl(order.label_url)}
                            sx={{ borderRadius: 2, fontWeight: 600, textTransform: "none" }}
                        >
                            Download Label
                        </Button>
                    )}

                    {/* Invoice Download */}
                    {/* {hasInvoice && (
                        <Button
                            variant="outlined"
                            color="info"
                            startIcon={<Receipt />}
                            onClick={() => openUrl(order.invoice_url)}
                            sx={{ borderRadius: 2, fontWeight: 600, textTransform: "none" }}
                        >
                            Download Invoice
                        </Button>
                    )} */}

                    {hasTracking && (
                        <Button
                            variant="outlined"
                            color="primary"
                            startIcon={<OpenInNew />}
                            onClick={() => openUrl(order.tracking_url)}
                            sx={{ borderRadius: 2, fontWeight: 600, textTransform: "none" }}
                        >
                            Track Shipment
                        </Button>
                    )}

                </Stack>
            </Box>

            {/* Tracking Info if AWB exists */}
            {hasAWB && (
                <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, mb: 2, bgcolor: alpha("#1976d2", 0.03) }}>
                    <SectionTitle icon={<FlightTakeoff sx={{ fontSize: 14 }} />}>Tracking Summary</SectionTitle>
                    <Stack direction="row" alignItems="center" spacing={2} flexWrap="wrap" gap={1}>
                        <Box>
                            <Typography variant="caption" color="text.secondary">AWB Number</Typography>
                            <Typography fontWeight={700} fontFamily="monospace" fontSize={16}>{order.awb_code}</Typography>
                        </Box>
                        {order.courier_name && (
                            <Box>
                                <Typography variant="caption" color="text.secondary">Courier</Typography>
                                <Typography fontWeight={600}>{order.courier_name}</Typography>
                            </Box>
                        )}
                        {order.estimated_delivery && (
                            <Box>
                                <Typography variant="caption" color="text.secondary">Est. Delivery</Typography>
                                <Typography fontWeight={600}>{fmtDate(order.estimated_delivery)}</Typography>
                            </Box>
                        )}
                    </Stack>
                </Paper>
            )}

            {/* Return / Refund Status */}
            {(order.return_status !== "none" || order.refund_status !== "none") && (
                <Alert
                    severity={order.return_status === "none" ? "info" : "warning"}
                    icon={<AssignmentReturn />}
                    sx={{ borderRadius: 2 }}
                >
                    <AlertTitle>Return & Refund</AlertTitle>
                    <Stack direction="row" spacing={2}>
                        <Typography variant="body2">Return: <strong>{order.return_status}</strong></Typography>
                        <Typography variant="body2">Refund: <strong>{order.refund_status}</strong></Typography>
                    </Stack>
                </Alert>
            )}
        </Box>
    );
}

// ─── Dispatch / Proof Upload ──────────────────────────────────────────────────

function DispatchTab({ selected, onRefresh }) {

    const parsedProofs = useMemo(() => parseJSON(selected?.dispatch_proof, []), [selected]);
    const vendorProofs = useMemo(() => parseJSON(selected?.vendorMediaProff, []), [selected]);
    const showCancel = canCancelOrder(selected);
    const [loading, setLoading] = useState(false)

    const handleSubmit = async () => {
        try {
            setLoading(true);
            await apiClient.put(`/api/admin/orders/${selected.id}/cancel`, {
                reason: 'Admin cancel order'
            });
            toast.success('Order cancel successfully!')
        } catch (err) {
            toast.error(err?.response?.data?.message || "Failed to cancel. Please try again.");
        } finally {
            onRefresh()
            setLoading(false);
        }
    };

    return (
        <Box>
            {showCancel &&
                <Button
                    variant="contained"
                    color={'error'}
                    startIcon={loading ? <CircularProgress size={14} color="inherit" /> : null}
                    disabled={loading}
                    onClick={() => handleSubmit()}
                    sx={{ borderRadius: 2, fontWeight: 600, textTransform: "none" }}
                >
                   Order Cancel 
                </Button>
            }

            {/* Existing Proofs */}
            {(parsedProofs.length > 0 || vendorProofs.length > 0) && (
                <Box sx={{ mb: 2 }}>
                    <SectionTitle icon={<Visibility sx={{ fontSize: 14 }} />}>Previously Uploaded Proof</SectionTitle>
                    <Stack direction="row" flexWrap="wrap" gap={1.5}>
                        {[...parsedProofs, ...vendorProofs].map((proof, i) => {
                            const url = typeof proof === "string" ? getImageFullUrl(proof) : getImageFullUrl(proof?.url || proof);
                            const isVideo = url?.match(/\.(mp4|mov|webm|avi)$/i);
                            return (
                                <Box
                                    key={i}
                                    component="a"
                                    href={url}
                                    target="_blank"
                                    sx={{ position: "relative", width: 90, height: 90, borderRadius: 2, overflow: "hidden", border: "1px solid", borderColor: "divider", display: "block", textDecoration: "none" }}
                                >
                                    {isVideo ? (
                                        <Box sx={{ width: "100%", height: "100%", bgcolor: "grey.200", display: "flex", alignItems: "center", justifyContent: "center" }}>
                                            <PlayArrow sx={{ color: "text.secondary" }} />
                                        </Box>
                                    ) : (
                                        <Box component="img" src={url} alt={`Proof ${i + 1}`} onError={(e) => (e.target.style.display = "none")} sx={{ width: "100%", height: "100%", objectFit: "cover" }} />
                                    )}
                                    <Box sx={{ position: "absolute", inset: 0, bgcolor: "rgba(0,0,0,0)", transition: "all .2s", "&:hover": { bgcolor: "rgba(0,0,0,0.3)" }, display: "flex", alignItems: "center", justifyContent: "center" }}>
                                        <OpenInNew sx={{ color: "#fff", fontSize: 18, opacity: 0, ".MuiBox-root:hover &": { opacity: 1 } }} />
                                    </Box>
                                </Box>
                            );
                        })}
                    </Stack>
                </Box>
            )}
        </Box>
    );
}

// ─── Main Component ───────────────────────────────────────────────────────────

const OrderDetails = () => {
    const { orderId } = useParams();
    const [activeTab, setActiveTab] = useState(0);
    const [selected, setSelected] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    const fetchDetails = useCallback(() => {
        setLoading(true);
        setError("");
        apiClient
            .get(`/api/admin/orders/${orderId}`)
            .then((res) => setSelected(res?.data?.data))
            .catch((err) => setError(err?.response?.data?.message || "Failed to load order."))
            .finally(() => setLoading(false));
    }, [orderId]);

    useEffect(() => {
        if (!orderId) return;
        fetchDetails();
    }, [orderId, fetchDetails]);

    const parsedItems = useMemo(() => parseJSON(selected?.items, []), [selected]);
    const parsedAddr = useMemo(() => parseJSON(selected?.delivery_address, {}), [selected]);
    console.log(parsedItems, 'ooo')
    if (loading) return <Box sx={{ p: 4 }}><LinearProgress /></Box>;
    if (error) return <Alert severity="error" sx={{ m: 4 }}>{error}</Alert>;
    if (!selected) return null;

    const TABS = ["Timeline", "Details", "Items", "Shipment Details"];

    return (
        <Paper sx={{ p: { xs: 2, md: 4 }, mx: "auto" }}>
            {/* Header */}
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap" gap={1} sx={{ mb: 2 }}>
                <Box>
                    <Stack direction="row" alignItems="center" spacing={1.5} flexWrap="wrap" gap={0.5}>
                        <Typography variant="h6" fontWeight={700}>{selected.orderNumber}</Typography>
                        <Chip
                            icon={STATUS_CONFIG[selected.order_status]?.icon}
                            label={STATUS_CONFIG[selected.order_status]?.label}
                            color={STATUS_CONFIG[selected.order_status]?.color}
                            size="small"
                            sx={{ fontWeight: 600 }}
                        />
                        <Chip
                            label={PAYMENT_CONFIG_TYPE[selected.payment_type]?.label}
                            color={PAYMENT_CONFIG_TYPE[selected.payment_type]?.color}
                            size="small"
                            variant="outlined"
                        />
                        {selected.awb_code && (
                            <Chip
                                icon={<QrCode2 sx={{ fontSize: 13 }} />}
                                label={`AWB: ${selected.awb_code}`}
                                size="small"
                                variant="outlined"
                                color="primary"
                                sx={{ fontFamily: "monospace", fontWeight: 600 }}
                            />
                        )}
                    </Stack>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                        {/* {selected.trackingId} */}
                        • {fmt(selected.createdAt)}
                    </Typography>
                </Box>
                <Tooltip title="Refresh">
                    <IconButton onClick={fetchDetails} size="small"><Refresh /></IconButton>
                </Tooltip>
            </Stack>

            {/* Shiprocket quick-info bar */}
            {selected.awb_code && (
                <Alert
                    severity="info"
                    icon={<Rocket />}
                    sx={{ mb: 2, borderRadius: 2 }}
                    action={
                        <Stack direction="row" spacing={1}>
                            {selected.label_url && (
                                <Button size="small" startIcon={<Label />} onClick={() => window.open(selected.label_url, "_blank")} sx={{ textTransform: "none" }}>Label</Button>
                            )}
                            {selected.tracking_url && (
                                <Button size="small" startIcon={<OpenInNew />} onClick={() => window.open(selected.tracking_url, "_blank")} sx={{ textTransform: "none" }}>Track</Button>
                            )}
                        </Stack>
                    }
                >
                    Shipment via <strong>{selected.courier_name || "Shiprocket"}</strong> — AWB: <strong style={{ fontFamily: "monospace" }}>{selected.awb_code}</strong>
                </Alert>
            )}

            {/* Tabs */}
            <Tabs
                value={activeTab}
                onChange={(_, v) => setActiveTab(v)}
                sx={{ borderBottom: 1, borderColor: "divider" }}
                variant="scrollable"
                scrollButtons="auto"
            >
                {TABS.map((t, i) => (
                    <Tab
                        key={t}
                        label={
                            t === "Shiprocket"
                                ? <Stack direction="row" alignItems="center" spacing={0.5}>
                                    <Rocket sx={{ fontSize: 14 }} />
                                    <span>{t}</span>
                                    {!selected.shiprocket_order_id && <FiberManualRecord sx={{ fontSize: 10, color: "warning.main" }} />}
                                </Stack>
                                : t
                        }
                        sx={{ fontSize: 13, textTransform: "none", fontWeight: 500 }}
                    />
                ))}
            </Tabs>

            <Box sx={{ mt: 3 }}>
                {/* Tab 0 – Timeline */}
                {activeTab === 0 && <OrderTimeline order={selected} />}

                {/* Tab 1 – Details */}
                {activeTab === 1 && (
                    <Grid container spacing={2}>
                        <Grid item size={{ xs: 12, sm: 6 }}>
                            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, height: "100%" }}>
                                <SectionTitle icon={<Email sx={{ fontSize: 14 }} />}>Customer</SectionTitle>
                                <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1.5 }}>
                                    <Avatar sx={{ bgcolor: "primary.light", color: "primary.dark", fontWeight: 700 }}>
                                        {initials(selected.customerName)}
                                    </Avatar>
                                    <Box>
                                        <Typography fontWeight={600}>{selected.customerName}</Typography>
                                        {/* <Typography variant="caption" color="text.secondary">{selected.customerEmail}</Typography> */}
                                    </Box>
                                </Stack>
                                <Divider sx={{ mb: 1.5 }} />
                                {[
                                    // [<Phone sx={{ fontSize: 15 }} />, "Mobile", selected.customerMobile],
                                    [null, "Method", selected.payment_type?.toUpperCase()],
                                    [null, "Payment ID", selected.paymentId],
                                ].filter(([, , v]) => v).map(([icon, label, value]) => (
                                    <Stack key={label} direction="row" alignItems="flex-start" spacing={1} sx={{ py: 0.7 }}>
                                        {icon && <Box sx={{ color: "text.secondary", mt: 0.2, flexShrink: 0 }}>{icon}</Box>}
                                        <Typography variant="body2" color="text.secondary" sx={{ minWidth: 90, flexShrink: 0 }}>{label}</Typography>
                                        <Typography variant="body2" fontWeight={500} sx={{ wordBreak: "break-all" }}>{value}</Typography>
                                    </Stack>
                                ))}
                            </Paper>
                        </Grid>

                        <Grid item size={{ xs: 12, sm: 6 }}>
                            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, height: "100%" }}>
                                <SectionTitle icon={<AttachMoney sx={{ fontSize: 14 }} />}>Payment Summary</SectionTitle>
                                {[
                                    ["Subtotal", `₹${parseFloat(selected.grand_total || 0).toLocaleString("en-IN")}`],
                                    // ["Tax", `₹${parseFloat(selected.tax_amount || 0).toLocaleString("en-IN")}`],
                                    ["Shipping Cost", `₹${parseFloat(selected.shipping_cost || 0).toLocaleString("en-IN")}`],
                                    // ["Discount", `-₹${parseFloat(selected.total_discount || 0).toLocaleString("en-IN")}`],
                                    // ["Coupon Discount", `-₹${parseFloat(selected.coupon_discount || 0).toLocaleString("en-IN")}`],
                                ].map(([k, v]) => (
                                    <Stack key={k} direction="row" justifyContent="space-between" sx={{ py: 0.6 }}>
                                        <Typography variant="body2" color="text.secondary">{k}</Typography>
                                        <Typography variant="body2">{v}</Typography>
                                    </Stack>
                                ))}
                                <Divider sx={{ my: 1 }} />
                                <Stack direction="row" justifyContent="space-between">
                                    <Typography fontWeight={700}>Grand Total</Typography>
                                    <Typography fontWeight={700} color="primary.main" fontSize={16}>
                                        ₹{parseFloat(selected.grand_total || 0).toLocaleString("en-IN")}
                                    </Typography>
                                </Stack>
                                <Box mt={1.5}>
                                    <Chip label={PAYMENT_CONFIG[selected.payment_status]?.label} color={PAYMENT_CONFIG[selected.payment_status]?.color} size="small" />
                                </Box>
                            </Paper>
                        </Grid>

                        <Grid item size={{ xs: 12 }}>
                            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                                <SectionTitle icon={<LocationOn sx={{ fontSize: 14 }} />}>Delivery Address</SectionTitle>
                                <Stack direction="row" spacing={1} alignItems="flex-start">
                                    <LocationOn sx={{ color: "text.secondary", mt: 0.3, flexShrink: 0 }} />
                                    <Box>
                                        <Typography variant="body2" fontWeight={500}>
                                            {parsedAddr.address_line1}{parsedAddr.landmark ? `, ${parsedAddr.landmark}` : ""}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            {[parsedAddr.city, parsedAddr.state, parsedAddr.country, parsedAddr.pincode].filter(Boolean).join(", ")}
                                        </Typography>
                                        <Stack direction="row" spacing={1} sx={{ mt: 0.75 }}>
                                            <Chip label={parsedAddr.type || "Home"} size="small" variant="outlined" />
                                            {/* {parsedAddr.mobile && (
                                                <Chip icon={<Phone sx={{ fontSize: 12 }} />} label={parsedAddr.mobile} size="small" variant="outlined" />
                                            )} */}
                                        </Stack>
                                    </Box>
                                </Stack>
                            </Paper>
                        </Grid>

                        <Grid item size={{ xs: 12, sm: 6 }}>
                            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                                <SectionTitle>Shipping Info</SectionTitle>
                                {[
                                    ["Method", selected.shipping_method],
                                    ["Pickup Type", selected.pickupType],
                                    ["Shipping Status", selected.shipping_status],
                                    ["Return Status", selected.return_status],
                                    ["Refund Status", selected.refund_status],
                                ].filter(([, v]) => v).map(([k, v]) => (
                                    <Stack key={k} direction="row" justifyContent="space-between" sx={{ py: 0.6 }}>
                                        <Typography variant="body2" color="text.secondary">{k}</Typography>
                                        <Typography variant="body2" fontWeight={500} sx={{ textTransform: "capitalize" }}>{v}</Typography>
                                    </Stack>
                                ))}
                            </Paper>
                        </Grid>

                        <Grid item size={{ xs: 12, sm: 6 }}>
                            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                                <SectionTitle>Flags</SectionTitle>
                                {[
                                    ["Is Paid", selected.isPaid],
                                    ["Is Delivered", selected.isDelivered],
                                    ["Return Request", selected.returnRequest],
                                ].map(([k, v]) => (
                                    <Stack key={k} direction="row" justifyContent="space-between" alignItems="center" sx={{ py: 0.6 }}>
                                        <Typography variant="body2" color="text.secondary">{k}</Typography>
                                        <Chip label={v ? "Yes" : "No"} color={v ? "success" : "default"} size="small" />
                                    </Stack>
                                ))}
                            </Paper>
                        </Grid>
                    </Grid>
                )}

                {/* Tab 2 – Items */}
                {activeTab === 2 && (
                    <Stack spacing={2}>
                        {parsedItems.map((item, i) => (
                            <Paper key={i} variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                                <Stack direction="row" spacing={2}>
                                    {item.thumbnail && (
                                        <Box
                                            component="img"
                                            src={getImageFullUrl(item.thumbnail)}
                                            alt={item.productName}
                                            onError={(e) => (e.target.style.display = "none")}
                                            sx={{ width: 72, height: 72, borderRadius: 2, objectFit: "cover", border: "1px solid", borderColor: "divider", flexShrink: 0 }}
                                        />
                                    )}
                                    <Box flex={1}>
                                        <Stack direction="row" justifyContent="space-between" flexWrap="wrap" gap={1}>
                                            <Box>
                                                <Typography fontWeight={600}>{item.productName}</Typography>
                                                <Typography variant="caption" color="text.secondary" display="block">SKU: {item.sku}</Typography>
                                                <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ mt: 0.75 }}>
                                                    {item.attributes?.map((a, j) => (
                                                        <Chip key={j} label={`${a.attributeName}: ${a.attributeValue}`} size="small" variant="outlined" sx={{ fontSize: 11 }} />
                                                    ))}
                                                </Stack>
                                            </Box>
                                            <Box textAlign="right">
                                                <Typography fontWeight={700} fontSize={16}>₹{parseFloat(item.price).toLocaleString("en-IN")}</Typography>
                                                <Typography variant="caption" color="text.secondary" display="block">Qty: {item.qty}</Typography>
                                                <Typography variant="body2" color="primary.main" fontWeight={600} display="block">
                                                    Total: ₹{item.total?.toLocaleString("en-IN")}
                                                </Typography>
                                                <Chip
                                                    label={STATUS_CONFIG[item.status]?.label || item.status}
                                                    color={STATUS_CONFIG[item.status]?.color || "default"}
                                                    size="small"
                                                    sx={{ mt: 0.5 }}
                                                />
                                            </Box>
                                        </Stack>
                                    </Box>
                                </Stack>
                            </Paper>
                        ))}
                    </Stack>
                )}

                {/* Tab 3 – Shiprocket */}
                {activeTab === 3 && <><DispatchTab selected={selected} onRefresh={fetchDetails} /> <br /><ShiprocketPanel order={selected} onRefresh={fetchDetails} /></>}

                {/* Tab 4 – Dispatch */}
                {/* {activeTab === 4 && <DispatchTab selected={selected} onRefresh={fetchDetails} />} */}
            </Box>
        </Paper>
    );
};

export default OrderDetails;